/**
 * ThreadListItem — one row in the chat history sidebar (STORY-002-010 step 2).
 *
 * Anatomy (mirrors the desktop prototype's thread rail —
 * `docs/GardEn/src/web-screens.jsx` WebChat history list): a left-aligned
 * button showing the thread title (or a localized "untitled" placeholder when
 * the title is null/empty) over a one-line redacted snippet, with an active
 * state (`primary-soft` fill + `primary-ink` text). A hover/focus action menu
 * exposes Rename / Pin / Delete.
 *
 * Snippet redaction (AC#13 / guardrail): the snippet comes ONLY from
 * `last_message_snippet` (the server's `content_redacted` projection). Any
 * `[REDACTED]` marker the redact worker left is rendered as the localized
 * `[…]` placeholder so users never see the literal phrase, and the raw content
 * never reaches the client at all.
 *
 * Tokens come from the Tailwind theme (`--g-*` mirror); no hand-rolled colors.
 * Strings come from the `chat` i18n namespace (RU/KY/EN first-class, ADR-028).
 */
import { MoreVertical, Pin, PinOff, Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { ThreadListItem as ThreadRow } from "@/features/chat/useThreadList";

/** The literal marker the backend redact worker leaves in `content_redacted`. */
const REDACTED_MARKER = "[REDACTED]";

export interface ThreadListItemProps {
  thread: ThreadRow;
  isActive: boolean;
  /**
   * Render on the imported design's dark rail (spec §4.2) rather than the
   * product's light one. Passed down by `ThreadList` — which reads the gate
   * once — rather than read here, so a rail cannot end up with rows in one
   * world and a panel in the other. Optional and false by default: every
   * existing caller and test mounts a row without it.
   */
  design?: boolean;
  onSelect: (threadId: string) => void;
  onRename: (threadId: string) => void;
  onTogglePin: (threadId: string, nextPinned: boolean) => void;
  onDelete: (threadId: string) => void;
  /** Keyboard navigation: arrow keys move focus, Enter activates (AC#10). */
  onKeyNav: (
    event: React.KeyboardEvent<HTMLButtonElement>,
    threadId: string,
  ) => void;
}

/** A heading, quote or list marker at the start of a line (repeats, as in `> -`). */
const MARKDOWN_LINE_MARKERS = /^[ \t]*(?:#{1,6}[ \t]+|>[ \t]?|[-*+][ \t]+)+/gm;
/** Bold markers and code backticks, anywhere. */
const MARKDOWN_INLINE_MARKERS = /\*\*|__|`+/g;

/**
 * Turn the server snippet into display text.
 *
 * The snippet is the start of the last message as the model wrote it, so it
 * carries the answer's Markdown: the rail read «Практичнее всего — **средне…»
 * (ui-review.md §4.2, B3). The strip keeps to what is only noise on one plain
 * line — bold and code markers anywhere, a heading, quote or list marker at the
 * start of a line — then collapses the whitespace. A lone `*` or `_`, a minus
 * sign and a numbered item are content and stay.
 *
 * Then every `[REDACTED]` marker becomes the localized `[…]` placeholder
 * (AC#13), after the strip so the placeholder is never altered. `null`/empty,
 * or nothing left once stripped → the "no preview" placeholder (worker hasn't
 * run, or the thread has no messages — story Risks).
 */
export function snippetToDisplay(
  snippet: string | null,
  redactedPlaceholder: string,
): string | null {
  if (snippet === null || snippet.length === 0) return null;
  const plain = snippet
    .replace(MARKDOWN_LINE_MARKERS, "")
    .replace(MARKDOWN_INLINE_MARKERS, "")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length === 0) return null;
  if (!plain.includes(REDACTED_MARKER)) return plain;
  return plain.split(REDACTED_MARKER).join(redactedPlaceholder);
}

/**
 * Resolve a UI language to a locale the runtime can actually format dates in.
 *
 * Chromium's ICU data has no `ky`: `new Intl.DateTimeFormat("ky").format()`
 * silently resolves to **en-US**, which is how a Kyrgyz chat rail ended up
 * labelling its rows "Thu". Kyrgyz shares Russian date conventions, so `ru` is
 * the correct fallback — far better than English in a Kyrgyz UI. (The same ICU
 * gap bites number formatting; see `features/marketplace/price-format.ts`.)
 *
 * Resolved once per session — locale negotiation is not free and the answer
 * cannot change while the page is open.
 */
let kyDateLocale: string | null = null;

/** The bare language subtag, e.g. `ru-RU` -> `ru`. */
function languageBase(language: string): string {
  return language.split("-")[0] ?? "ru";
}

export function dateLocaleFor(language: string): string {
  const base = language.split("-")[0] ?? "ru";
  if (base !== "ky") return language;
  if (kyDateLocale === null) {
    let resolved = "ru";
    try {
      const actual = new Intl.DateTimeFormat("ky").resolvedOptions().locale;
      if (actual.toLowerCase().startsWith("ky")) resolved = "ky";
    } catch {
      // Intl unavailable or the tag was rejected — the ru default stands.
    }
    kyDateLocale = resolved;
  }
  return kyDateLocale;
}

/**
 * A short, locale-aware "when" for a thread row — `14:32` for today, `Пн` this
 * week, `12.08` this year, `08.24` beyond it. Exported so the truncation rules
 * are unit-testable without rendering.
 *
 * Deliberately terse: this sits in a 220px rail beside a title that needs the
 * room, and its whole job is to make two adjacent rows distinguishable at a
 * glance. Rendered in `.g-data` at the call site so the column aligns.
 */
export function relativeStamp(iso: string | null, language: string): string {
  if (iso === null) return "";
  const then = new Date(iso);
  const ms = then.getTime();
  if (Number.isNaN(ms)) return "";
  const dateLocale = dateLocaleFor(language);

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();

  if (ms >= startOfToday) {
    return then.toLocaleTimeString(dateLocale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  if (ms >= startOfToday - 6 * 24 * 60 * 60 * 1000) {
    const time = then.toLocaleTimeString(dateLocale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    // A WEEKDAY WORD only when the runtime can say it in the UI language.
    //
    // `dateLocaleFor` falls back to `ru` for Kyrgyz because Chromium ships no
    // `ky` ICU data — which is right for digits, and wrong for words: it put a
    // Russian "чт" underneath a Kyrgyz "БҮГҮН" bucket header, one mixed-language
    // line in the rail. When the weekday cannot be said in the user's language,
    // the stamp goes numeric (`13.08 13:04`), which reads correctly in all
    // three and still separates two rows from the same day.
    if (dateLocaleFor(language) === languageBase(language)) {
      const day = then.toLocaleDateString(dateLocale, { weekday: "short" });
      return `${day} ${time}`;
    }
    const date = then.toLocaleDateString(dateLocale, {
      day: "2-digit",
      month: "2-digit",
    });
    return `${date} ${time}`;
  }
  if (then.getFullYear() === now.getFullYear()) {
    return then.toLocaleDateString(dateLocale, {
      day: "2-digit",
      month: "2-digit",
    });
  }
  return then.toLocaleDateString(dateLocale, {
    month: "2-digit",
    year: "2-digit",
  });
}

export function ThreadListItem({
  thread,
  isActive,
  design = false,
  onSelect,
  onRename,
  onTogglePin,
  onDelete,
  onKeyNav,
}: ThreadListItemProps): ReactElement {
  const { t, i18n } = useTranslation("chat");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the action menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const title =
    thread.title !== null && thread.title.length > 0
      ? thread.title
      : t("sidebar.untitled");

  const snippet = snippetToDisplay(
    thread.last_message_snippet,
    t("sidebar.snippetRedacted"),
  );

  /*
   * The second line used to be the snippet OR the literal string "Нет
   * сообщений", which on a seeded account produced twelve consecutive rows
   * reading "Без названия / Нет сообщений" — an unreadable wall where the only
   * way to tell two threads apart was their position. It now leads with WHEN,
   * which is always distinct between two rows.
   *
   * THREE states, not two, because a missing snippet does not mean an empty
   * thread. `last_message_snippet` comes from the server's `content_redacted`
   * projection, written by a worker that can lag or never have run — this dev
   * account has twenty threads with a real `last_message_at` and a null
   * snippet. Labelling those "черновик" told the user their conversations were
   * empty drafts, which is precisely the kind of confident-but-wrong statement
   * this product must not make:
   *
   *   snippet present            → time · snippet
   *   no snippet, but has spoken → time alone (a real thread, preview pending)
   *   never spoken               → time · draft
   */
  const hasSpoken = thread.last_message_at !== null;
  const stamp = relativeStamp(
    thread.last_message_at ?? thread.created_at,
    i18n.language,
  );
  /** The design rail's subtitle ink (time, snippet, draft) — see D1 below. */
  const subtitleInk = isActive
    ? "text-[var(--gd-panel-ink-2)]"
    : "text-[var(--gd-panel-ink-3)]";

  return (
    <div className="group/threaditem relative" ref={menuRef}>
      <button
        type="button"
        role="option"
        aria-selected={isActive}
        aria-current={isActive ? "page" : undefined}
        onClick={() => onSelect(thread.id)}
        onKeyDown={(e) => onKeyNav(e, thread.id)}
        data-testid={`chat-thread-${thread.id}`}
        data-thread-id={thread.id}
        className={
          design
            ? // Spec §4.2: radius 10, `11px 12px`, two lines. The active state
              // carries three channels of its own here — the `--gd-panel-active`
              // fill, the 600 weight and the brighter `--gd-on-primary` ink — so
              // it does not also take the incumbent's margin rule, which the
              // design does not draw.
              "relative flex min-h-[52px] w-full flex-col gap-1 rounded-[10px] py-[11px] pl-3 pr-9 text-left transition-colors duration-150 " +
              (isActive
                ? "bg-[var(--gd-panel-active)]"
                : "hover:bg-[var(--gd-panel-hover)]")
            : "relative flex min-h-[52px] w-full flex-col gap-1 rounded-[10px] py-2.5 pl-3 pr-9 text-left transition-colors duration-150 " +
              (isActive
                ? // Not `--g-primary-soft`: in dark that token is the brand at 20%
                  // over the rail, which composites to only 1.14:1 against it —
                  // technically lighter (elevation is not inverted, measured), but
                  // too close to read as selected at a glance. Mixed at 34% here,
                  // and the state never rests on the fill alone: the 1px rule in
                  // the margin and the ink change are two more channels.
                  "bg-[color-mix(in_srgb,var(--g-primary)_34%,transparent)] text-[var(--g-primary-ink)]"
                : "text-foreground hover:bg-[var(--g-sunken)]")
        }
      >
        {/* The active marker is a 1px drawn rule in the margin, not a fill
            alone — it survives at any contrast and reads as a ledger mark. */}
        {isActive && !design ? (
          <span
            aria-hidden="true"
            className="absolute inset-y-2 left-0 w-px bg-[var(--g-primary)]"
          />
        ) : null}
        <span className="flex items-center gap-1.5">
          {thread.is_pinned && (
            <Pin
              className={
                "size-3 shrink-0 " +
                (design ? "text-[var(--gd-accent-on-dark)]" : "text-primary")
              }
              aria-hidden="true"
            />
          )}
          <span
            className={
              "truncate text-[14.5px] leading-snug " +
              (design
                ? isActive
                  ? "font-semibold text-[var(--gd-on-primary)]"
                  : "font-medium text-[var(--gd-panel-ink-2)]"
                : "font-semibold")
            }
          >
            {title}
          </span>
        </span>
        {/*
         * The second line leads with WHEN, then the snippet — a deliberate
         * deviation from spec §4.2, which draws the snippet alone.
         *
         * `last_message_snippet` is written by a worker that can lag or never
         * have run: this dev account has twenty threads with a real
         * `last_message_at` and a null snippet. Snippet-only would render
         * those as a column of identical untitled rows, which is the defect
         * the timestamp was added to fix. The design's treatment is kept —
         * one 11.5px subtitle line in `--gd-panel-ink-3` — and the time rides
         * inside it.
         *
         * On the ACTIVE row that ink sits on the `--gd-panel-active` lift and
         * measured 4.26:1 (D1, ui-review.md §4.7), so the open thread's line
         * steps up to `--gd-panel-ink-2` (6.57:1). Other rows keep -3 (5.00:1).
         */}
        <span className="flex min-w-0 items-baseline gap-1.5 text-[12.5px] leading-none">
          <span
            className={
              "g-data shrink-0 " +
              (design ? subtitleInk : "text-[var(--g-ink-2)]")
            }
          >
            {stamp}
          </span>
          {snippet !== null ? (
            <>
              <span
                aria-hidden="true"
                className={
                  design
                    ? "text-[var(--gd-panel-ink-4)]"
                    : "text-[var(--g-ink-3)]"
                }
              >
                ·
              </span>
              <span
                className={
                  "truncate " +
                  (design ? subtitleInk : "text-[var(--g-ink-2)]")
                }
              >
                {snippet}
              </span>
            </>
          ) : hasSpoken ? null : (
            <>
              <span
                aria-hidden="true"
                className={
                  design
                    ? "text-[var(--gd-panel-ink-4)]"
                    : "text-[var(--g-ink-3)]"
                }
              >
                ·
              </span>
              <span
                className={
                  "truncate " +
                  (design ? subtitleInk : "text-[var(--g-ink-2)]")
                }
              >
                {t("sidebar.draft")}
              </span>
            </>
          )}
        </span>
      </button>

      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t("actions.menu")}
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        data-testid={`chat-thread-menu-${thread.id}`}
        className={
          "absolute right-1 top-2 flex size-8 items-center justify-center rounded-[8px] opacity-0 transition-opacity duration-150 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 group-hover/threaditem:opacity-100 " +
          (design
            ? "text-[var(--gd-panel-ink-3)] hover:bg-[var(--gd-panel-hover)] hover:text-[var(--gd-panel-ink)] focus-visible:ring-[var(--gd-accent-on-dark)]"
            : "text-[var(--g-ink-3)] hover:bg-[var(--g-elevated)] hover:text-foreground focus-visible:ring-[var(--g-primary)]")
        }
      >
        <MoreVertical className="size-4" aria-hidden="true" />
      </button>

      {menuOpen && (
        <div
          role="menu"
          aria-label={t("actions.menu")}
          data-testid={`chat-thread-actions-${thread.id}`}
          className="absolute right-1 top-9 z-10 min-w-[160px] overflow-hidden rounded-[10px] border border-border bg-card py-1 shadow-md"
        >
          <ThreadMenuItem
            icon={<Pencil className="size-4" aria-hidden="true" />}
            label={t("actions.rename")}
            testId={`chat-thread-rename-${thread.id}`}
            onClick={() => {
              setMenuOpen(false);
              onRename(thread.id);
            }}
          />
          <ThreadMenuItem
            icon={
              thread.is_pinned ? (
                <PinOff className="size-4" aria-hidden="true" />
              ) : (
                <Pin className="size-4" aria-hidden="true" />
              )
            }
            label={thread.is_pinned ? t("actions.unpin") : t("actions.pin")}
            testId={`chat-thread-pin-${thread.id}`}
            onClick={() => {
              setMenuOpen(false);
              onTogglePin(thread.id, !thread.is_pinned);
            }}
          />
          <ThreadMenuItem
            icon={<Trash2 className="size-4" aria-hidden="true" />}
            label={t("actions.delete")}
            testId={`chat-thread-delete-${thread.id}`}
            destructive
            onClick={() => {
              setMenuOpen(false);
              onDelete(thread.id);
            }}
          />
        </div>
      )}
    </div>
  );
}

function ThreadMenuItem({
  icon,
  label,
  testId,
  onClick,
  destructive = false,
}: {
  icon: ReactElement;
  label: string;
  testId: string;
  onClick: () => void;
  destructive?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testId}
      className={
        "flex min-h-[40px] w-full items-center gap-2.5 px-3 text-left text-[15px]/[1.429] transition-colors hover:bg-secondary " +
        (destructive ? "text-destructive" : "text-foreground")
      }
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export default ThreadListItem;
